import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  DatePicker,
  Input,
  InputNumber,
  Menu,
  Message,
  Modal,
  Popover,
  Panel,
  SelectPicker,
  Stack,
  Table,
  toaster,
  Tooltip,
  Whisper,
} from 'rsuite';

import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

import {
  useActiveCloseDateSource,
  useActiveProvider,
  useCloseDateSources,
  useCloseDates,
  useDeleteCloseDate,
  useDeleteUser,
  usePermanentDeleteProject,
  useProjects,
  useProviders,
  useRates,
  useRestoreProject,
  useSetActiveProvider,
  useSetActiveCloseDateSource,
  useTrashedProjects,
  useUpsertCloseDate,
  useUpsertUser,
  useUsers,
} from '../hooks/useQueries';

import './ConfigurationPage.css';

const { Column, HeaderCell, Cell } = Table;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CurrentBadge = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1px 8px',
      borderRadius: 999,
      border: '1px solid #b7eb8f',
      background: '#f6ffed',
      color: '#389e0d',
      fontSize: 11,
      fontWeight: 800,
      lineHeight: '16px',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
    }}
  >
    Current
  </span>
);

export default function ConfigurationPage() {
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizeBaseUrl = (raw) => {
    if (!raw) return '';
    return String(raw).trim().replace(/\/+$/, '');
  };

  const ensureApiPrefix = (path) => {
    const p = String(path || '');
    if (!p.startsWith('/')) return `/api/${p}`;
    return p.startsWith('/api/') || p === '/api' ? p : `/api${p}`;
  };

  const buildApiUrl = (endpoint) => {
    const base = normalizeBaseUrl(API_CONFIG.BASE_URL);
    const path = ensureApiPrefix(endpoint);
    if (!base) return path;
    const baseNoApi = base.endsWith('/api') ? base.slice(0, -4) : base;
    if (base.startsWith('/')) {
      if (base === '/api' && path.startsWith('/api')) return path;
      return `${baseNoApi}${path}`;
    }
    return `${baseNoApi}${path}`;
  };

  const { data: users, isLoading, error } = useUsers();
  const upsertUser = useUpsertUser();
  const deleteUser = useDeleteUser();

  const { data: projects, isLoading: projectsLoading, error: projectsError } = useProjects();
  const [ratesProjectId, setRatesProjectId] = useState(null);
  const [ratesOnlyCurrent, setRatesOnlyCurrent] = useState(false);
  const { data: ratesRes, isLoading: ratesLoading, error: ratesError } = useRates(ratesProjectId);

  const {
    data: closeDatesRes,
    isLoading: closeDatesLoading,
    error: closeDatesError,
    refetch: refetchCloseDates,
  } = useCloseDates();
  const { data: closeDateSourcesRes, isLoading: closeDateSourcesLoading } = useCloseDateSources();
  const { data: activeCloseDateSource, isLoading: activeCloseDateSourceLoading } = useActiveCloseDateSource();
  const setActiveCloseDateSource = useSetActiveCloseDateSource();
  const upsertCloseDate = useUpsertCloseDate();
  const deleteCloseDate = useDeleteCloseDate();
  const { data: trashedProjectsRes, isLoading: trashedProjectsLoading, error: trashedProjectsError } = useTrashedProjects();
  const restoreProject = useRestoreProject();
  const permanentDeleteProject = usePermanentDeleteProject();

  const { data: providers, isLoading: providersLoading } = useProviders();
  const { data: activeProvider } = useActiveProvider();
  const setActiveProvider = useSetActiveProvider();

  const [selectedProviderName, setSelectedProviderName] = useState('');
  const [providerConfig, setProviderConfig] = useState({});
  const [showProviderConfig, setShowProviderConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [selectedCloseDateSourceName, setSelectedCloseDateSourceName] = useState('');
  const [showCloseDateSourceConfig, setShowCloseDateSourceConfig] = useState(false);
  const [pullingEmployeeConnectCloseDates, setPullingEmployeeConnectCloseDates] = useState(false);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserAadOid, setNewUserAadOid] = useState('');

  const [addCloseDateOpen, setAddCloseDateOpen] = useState(false);
  const [newCloseDate, setNewCloseDate] = useState(null);
  const [newCloseDesc, setNewCloseDesc] = useState('');

  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserField, setEditingUserField] = useState(null); // 'name' | 'email' | 'reserved'
  const [editingUserName, setEditingUserName] = useState('');
  const [editingUserEmail, setEditingUserEmail] = useState('');
  const editingUserEmailDraftRef = useRef('');
  const [editingUserReservedPercent, setEditingUserReservedPercent] = useState(0);

  const [showReservedHoursInfo, setShowReservedHoursInfo] = useState(false);

  const [confirmDeleteUserOpen, setConfirmDeleteUserOpen] = useState(false);
  const [confirmDeleteUserRow, setConfirmDeleteUserRow] = useState(null);

  const [confirmDeleteCloseDateOpen, setConfirmDeleteCloseDateOpen] = useState(false);
  const [confirmDeleteCloseDateRow, setConfirmDeleteCloseDateRow] = useState(null);
  const [confirmPermDeleteOpen, setConfirmPermDeleteOpen] = useState(false);
  const [confirmPermDeleteRow, setConfirmPermDeleteRow] = useState(null);

  const [editingCloseDateId, setEditingCloseDateId] = useState(null);
  const [editingCloseDateIso, setEditingCloseDateIso] = useState(null);
  const [editingCloseDateDesc, setEditingCloseDateDesc] = useState('');

  const providerRows = useMemo(() => (Array.isArray(providers) ? providers : []), [providers]);
  const projectRows = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);
  const ratesRows = useMemo(() => {
    const rows = Array.isArray(ratesRes) ? ratesRes : [];
    return rows
      .map((r) => ({
        ...r,
        key: `${r?.projectId ?? ''}-${r?.userId ?? ''}-${String(r?.effectiveDate || '')}`,
      }))
      .sort((a, b) => {
        const pa = String(a?.projectName ?? '');
        const pb = String(b?.projectName ?? '');
        const pc = pa.localeCompare(pb);
        if (pc !== 0) return pc;
        const ua = String(a?.userName ?? '');
        const ub = String(b?.userName ?? '');
        const uc = ua.localeCompare(ub);
        if (uc !== 0) return uc;
        return String(b?.effectiveDate ?? '').localeCompare(String(a?.effectiveDate ?? ''));
      });
  }, [ratesRes]);

  const currentEffectiveDateByUserProject = useMemo(() => {
    const map = new Map();
    for (const r of ratesRows) {
      const uid = Number(r?.userId);
      const pid = Number(r?.projectId);
      const eff = String(r?.effectiveDate ?? '').slice(0, 10);
      if (!Number.isInteger(uid) || !Number.isInteger(pid)) continue;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) continue;
      const key = `${uid}-${pid}`;
      const prev = map.get(key);
      if (!prev || String(eff).localeCompare(String(prev)) > 0) {
        map.set(key, eff);
      }
    }
    return map;
  }, [ratesRows]);

  const visibleRatesRows = useMemo(() => {
    if (!ratesOnlyCurrent) return ratesRows;
    return ratesRows.filter((r) => {
      const uid = Number(r?.userId);
      const pid = Number(r?.projectId);
      const eff = String(r?.effectiveDate ?? '').slice(0, 10);
      if (!Number.isInteger(uid) || !Number.isInteger(pid)) return false;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) return false;
      const key = `${uid}-${pid}`;
      const currentEff = currentEffectiveDateByUserProject.get(key);
      return Boolean(currentEff) && eff === currentEff;
    });
  }, [ratesOnlyCurrent, ratesRows, currentEffectiveDateByUserProject]);

  const openProviderConfig = (providerName) => {
    const name = String(providerName || '').trim();
    if (!name) return;

    setSelectedProviderName(name);

    if (activeProvider?.name === name && activeProvider?.config && typeof activeProvider.config === 'object') {
      setProviderConfig(activeProvider.config);
    } else {
      const schema = providerRows.find((p) => p?.name === name)?.configSchema || {};
      const initial = {};
      for (const k of Object.keys(schema)) {
        initial[k] = '';
      }
      setProviderConfig(initial);
    }

    setShowProviderConfig(true);
  };

  const testProviderConnection = async () => {
    const name = String(selectedProviderName || '').trim();
    if (!name) return;

    setTestingConnection(true);
    try {
      const url = buildApiUrl(`/providers/${encodeURIComponent(name)}/test`);
      const resp = await authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify({ config: providerConfig || {} })
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(json?.error || `HTTP ${resp.status}`);
      }

      if (json?.success) {
        toaster.push(
          <Message type="success" showIcon closable>
            {json?.message || 'Connection successful.'}
          </Message>,
          { placement: 'topEnd', duration: 4500 }
        );
      } else {
        toaster.push(
          <Message type="error" showIcon closable>
            {json?.message || 'Connection failed.'}
          </Message>,
          { placement: 'topEnd', duration: 4500 }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to test provider connection.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    } finally {
      setTestingConnection(false);
    }
  };

  const saveProviderConfig = async () => {
    const name = String(selectedProviderName || '').trim();
    if (!name) return;

    const schema = providerRows.find((p) => p?.name === name)?.configSchema || {};
    for (const [key, def] of Object.entries(schema)) {
      if (def?.required) {
        const v = providerConfig?.[key];
        if (typeof v !== 'string' || v.trim().length === 0) {
          toaster.push(
            <Message type="warning" showIcon closable>
              Please provide: {def?.label || key}
            </Message>,
            { placement: 'topEnd', duration: 4500 }
          );
          return;
        }
      }
    }

    try {
      await setActiveProvider.mutateAsync({ providerName: name, config: providerConfig });
      toaster.push(
        <Message type="success" showIcon closable>
          Hours provider activated successfully.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
      setShowProviderConfig(false);
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to activate provider.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    }
  };

  const userRows = useMemo(() => (Array.isArray(users) ? users : []), [users]);

  const closeDateRows = useMemo(() => {
    const normalizeIso10 = (raw) => {
      if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        const yyyy = raw.getFullYear();
        const mm = String(raw.getMonth() + 1).padStart(2, '0');
        const dd = String(raw.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }

      const s0 = String(raw ?? '').trim();
      if (!s0) return null;

      // "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS..."
      const s10 = s0.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s10)) return s10;

      // "MM/DD/YYYY"
      const mdy = s0.match(/^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/);
      if (mdy) {
        const mm = String(mdy[1]).padStart(2, '0');
        const dd = String(mdy[2]).padStart(2, '0');
        return `${mdy[3]}-${mm}-${dd}`;
      }

      return null;
    };

    const formatUsDate = (iso10) => {
      const s = String(iso10 || '').slice(0, 10);
      const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
      if (!m) return s;
      return `${m[2]}/${m[3]}`;
    };

    const arr = Array.isArray(closeDatesRes) ? closeDatesRes : [];
    return arr
      .map((r) => ({
        id: r?.id,
        closeDateIso: normalizeIso10(r?.closeDate ?? r?.close_date),
        closeDate: formatUsDate(normalizeIso10(r?.closeDate ?? r?.close_date)),
        description: String(r?.description ?? ''),
      }))
      .map((r) => ({
        ...r,
        key: String(r.id ?? r.closeDateIso ?? Math.random()),
      }))
      .filter((r) => r.closeDateIso)
      .sort((a, b) => String(a.closeDateIso).localeCompare(String(b.closeDateIso)));
  }, [closeDatesRes]);

  const closeDateSourceRows = useMemo(
    () => (Array.isArray(closeDateSourcesRes) ? closeDateSourcesRes : []),
    [closeDateSourcesRes]
  );

  const activeCloseDateSourceName = String(activeCloseDateSource?.name || 'manual');
  const activeCloseDateSourceDisplayName =
    String(activeCloseDateSource?.displayName || (activeCloseDateSourceName === 'employeeconnect' ? 'EmployeeConnect' : 'Manual (Built-in)'));
  const isManualCloseDateSource = activeCloseDateSourceName === 'manual';
  const selectedCloseDateSource = useMemo(
    () => closeDateSourceRows.find((s) => String(s?.name || '') === String(selectedCloseDateSourceName || '')) || null,
    [closeDateSourceRows, selectedCloseDateSourceName]
  );
  const selectedSourceIsEmployeeConnect = String(selectedCloseDateSourceName || '').toLowerCase() === 'employeeconnect';

  useEffect(() => {
    if (activeCloseDateSourceName) {
      setSelectedCloseDateSourceName(activeCloseDateSourceName);
    }
  }, [activeCloseDateSourceName]);

  const trashedProjects = useMemo(() => {
    const rows = Array.isArray(trashedProjectsRes) ? trashedProjectsRes : [];
    return rows
      .map((r) => {
        const id = Number(r?.id);
        if (!Number.isInteger(id)) return null;
        return {
          id,
          name: String(r?.name ?? '').trim() || `Project ${id}`,
          deletedAt: r?.deletedAt || null,
          trashExpiresAt: r?.trashExpiresAt || null,
        };
      })
      .filter(Boolean);
  }, [trashedProjectsRes]);

  const formatTrashDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  };

  const openAddUser = () => {
    setNewUserName('');
    setNewUserEmail('');
    setNewUserAadOid('');
    setAddUserOpen(true);
  };

  const openAddCloseDate = () => {
    setNewCloseDate(null);
    setNewCloseDesc('');
    setAddCloseDateOpen(true);
  };

  const submitAddUser = async () => {
    const name = String(newUserName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please enter a user name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const email = String(newUserEmail || '').trim();
    const aadOid = String(newUserAadOid || '').trim();

    const created = await upsertUser.mutateAsync({ name, email, aadOid });
    toaster.push(
      <Message type="success" showIcon>
        User created.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );

    setAddUserOpen(false);
    setNewUserName('');
    setNewUserEmail('');
    setNewUserAadOid('');

    const createdId = Number(created?.id);
    if (Number.isInteger(createdId)) {
      navigate(`/users/${createdId}`);
    }
  };

  const toIsoDate10 = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    // Close dates are treated as recurring annual dates; store with a fixed year.
    const yyyy = 2000;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const submitAddCloseDate = async () => {
    const iso = toIsoDate10(newCloseDate);
    if (!iso) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a close date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await upsertCloseDate.mutateAsync({ closeDate: iso, description: String(newCloseDesc || '').trim() });
    toaster.push(
      <Message type="success" showIcon>
        Close date saved.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );

    setAddCloseDateOpen(false);
    setNewCloseDate(null);
    setNewCloseDesc('');
  };

  const beginEditUser = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setEditingUserId(id);
    setEditingUserField('name');
    setEditingUserName(String(row?.name ?? ''));
    const email = String(row?.email ?? '');
    setEditingUserEmail(email);
    editingUserEmailDraftRef.current = email;
  };

  const beginEditUserEmail = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setEditingUserId(id);
    setEditingUserField('email');
    setEditingUserName(String(row?.name ?? ''));
    const email = String(row?.email ?? '');
    setEditingUserEmail(email);
    editingUserEmailDraftRef.current = email;
  };

  const beginEditUserFte = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setEditingUserId(id);
    setEditingUserField('reserved');
    setEditingUserName(String(row?.name ?? ''));
    const email = String(row?.email ?? '');
    setEditingUserEmail(email);
    editingUserEmailDraftRef.current = email;
    const raw = Number(row?.ftePercent ?? row?.fte_percent ?? 100);
    const fte = Number.isFinite(raw) ? raw : 100;
    const reserved = 100 - fte;
    setEditingUserReservedPercent(Number.isFinite(reserved) ? reserved : 0);
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
    setEditingUserField(null);
    setEditingUserName('');
    setEditingUserEmail('');
    editingUserEmailDraftRef.current = '';
    setEditingUserReservedPercent(0);
  };

  const commitEditUser = async () => {
    const id = Number(editingUserId);
    if (!Number.isInteger(id)) {
      cancelEditUser();
      return;
    }

    const name = String(editingUserName || '').trim();
    const email = String(editingUserEmailDraftRef.current || editingUserEmail || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Name cannot be empty.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
      return;
    }

    const rawReserved = Number(editingUserReservedPercent);
    const reservedPercent = Number.isFinite(rawReserved)
      ? Math.min(100, Math.max(0, rawReserved))
      : 0;
    const ftePercent = 100 - reservedPercent;

    if (editingUserField === 'reserved') {
      await upsertUser.mutateAsync({ id, name, email, ftePercent });
    } else {
      await upsertUser.mutateAsync({ id, name, email });
    }
    toaster.push(
      <Message type="success" showIcon>
        User saved.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );
    cancelEditUser();
  };

  const iso10ToDate = (iso10) => {
    const s = String(iso10 || '').slice(0, 10);
    const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
    if (!m) return null;
    const yyyy = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);
    if (!Number.isFinite(yyyy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
    const d = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const beginEditCloseDate = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setEditingCloseDateId(id);
    setEditingCloseDateIso(String(row?.closeDateIso ?? row?.closeDate ?? ''));
    setEditingCloseDateDesc(String(row?.description ?? ''));
  };

  const cancelEditCloseDate = () => {
    setEditingCloseDateId(null);
    setEditingCloseDateIso(null);
    setEditingCloseDateDesc('');
  };

  const commitEditCloseDate = async (overrides = {}) => {
    const id = Number(overrides?.id ?? editingCloseDateId);
    if (!Number.isInteger(id)) {
      cancelEditCloseDate();
      return;
    }

    const targetIso = String(overrides?.closeDateIso ?? editingCloseDateIso ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(targetIso)) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a close date.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
      return;
    }

    const description = String(overrides?.description ?? editingCloseDateDesc ?? '').trim();

    // If date changed, delete old row then upsert the new one (server upsert key is close_date).
    const existing = closeDateRows.find((r) => Number(r?.id) === id);
    const existingIso = String(existing?.closeDateIso || '').slice(0, 10);

    if (existingIso && existingIso !== targetIso) {
      await deleteCloseDate.mutateAsync(id);
    }

    await upsertCloseDate.mutateAsync({ closeDate: targetIso, description });

    toaster.push(
      <Message type="success" showIcon>
        Close date saved.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );

    cancelEditCloseDate();
  };

  const goToUser = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    navigate(`/users/${id}`);
  };

  const requestDeleteUser = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setConfirmDeleteUserRow(row);
    setConfirmDeleteUserOpen(true);
  };

  const removeUser = async (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;

    await deleteUser.mutateAsync(id);
    toaster.push(
      <Message type="success" showIcon>
        User removed.
      </Message>,
      { placement: 'topEnd', duration: 2500 }
    );
  };

  const requestDeleteCloseDate = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setConfirmDeleteCloseDateRow(row);
    setConfirmDeleteCloseDateOpen(true);
  };

  const removeCloseDate = async (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;

    await deleteCloseDate.mutateAsync(id);
    toaster.push(
      <Message type="success" showIcon>
        Close date removed.
      </Message>,
      { placement: 'topEnd', duration: 2500 }
    );
  };

  const saveCloseDateSource = async () => {
    const sourceName = String(selectedCloseDateSourceName || '').trim().toLowerCase();
    if (!sourceName) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a close date source.
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );
      return;
    }

    await setActiveCloseDateSource.mutateAsync({ sourceName });

    toaster.push(
      <Message type="success" showIcon>
        Close date source updated.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );
  };

  const pullFromEmployeeConnect = async () => {
    if (!selectedSourceIsEmployeeConnect) return;

    setPullingEmployeeConnectCloseDates(true);
    try {
      const currentYear = new Date().getFullYear();
      const qs = new URLSearchParams();
      qs.set('startYear', String(currentYear));
      qs.set('yearCount', '2');

      const url = buildApiUrl(`/connect/employeeconnect/close-dates?${qs.toString()}`);
      const resp = await authenticatedFetch(url, { method: 'GET' });
      const json = await resp.json().catch(() => null);

      if (!resp.ok) {
        throw new Error(String(json?.message || json?.error || `Request failed (${resp.status})`));
      }

      const count = Array.isArray(json?.data) ? json.data.length : 0;
      toaster.push(
        <Message type="success" showIcon>
          Pulled {count} close date definition{count === 1 ? '' : 's'} from EmployeeConnect.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );

      await refetchCloseDates();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {String(err?.message || 'Failed to pull close dates from EmployeeConnect.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    } finally {
      setPullingEmployeeConnectCloseDates(false);
    }
  };

  return (
    <div className="ppc-page">
      <Panel bordered header="Configuration">
        <Divider />

        {error ? (
          <Message type="error" showIcon>
            Failed to load users.
          </Message>
        ) : null}

        <Panel
          bordered
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>Hours Source</span>
            </div>
          }
          style={{ marginBottom: 12 }}
        >
          <div style={{ marginTop: 12 }}>
            {activeProvider?.name ? (
              <Stack direction="row" spacing={8} alignItems="center">
                <span style={{ fontWeight: 700 }}>Active:</span>
                <span
                  className="ppc-provider-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 10px',
                    borderRadius: 999,
                    border: '1px solid #e6e6e6',
                    background: '#fafafa',
                  }}
                >
                  <span
                    style={{
                      color: activeProvider?.isConfigured ? '#28a745' : '#f39c12',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                    aria-label={activeProvider?.isConfigured ? 'Configured' : 'Not configured'}
                  >
                    {activeProvider?.isConfigured ? (
                      <i className="bi bi-check-circle-fill" aria-hidden="true" />
                    ) : (
                      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                    )}
                  </span>
                  <span>{activeProvider.displayName || activeProvider.name}</span>
                </span>
              </Stack>
            ) : (
              <Message type="info" showIcon style={{ marginTop: 12 }}>
                No hours provider configured.
              </Message>
            )}
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <Stack direction="column" spacing={8}>
            <div style={{ fontSize: 12, color: '#666' }}>Provider</div>
            <SelectPicker
              block
              searchable={false}
              cleanable
              placeholder="Select a provider..."
              data={providerRows.map((p) => ({ label: p.displayName, value: p.name }))}
              value={selectedProviderName}
              onChange={(v) => setSelectedProviderName(v || '')}
              loading={providersLoading}
            />

            <Button
              appearance="primary"
              disabled={!selectedProviderName}
              onClick={() => openProviderConfig(selectedProviderName)}
            >
              Configure &amp; Activate
            </Button>
          </Stack>
        </Panel>

        <Panel
          bordered
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>Users</span>
              <Button appearance="subtle" size="sm" onClick={openAddUser}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Add
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
              </Button>
            </div>
          }
        >
          <Table
            data={userRows}
            rowKey="id"
            autoHeight
            bordered
            cellBordered
            loading={isLoading}
            className="ppc-config-users-table"
          >
            <Column flexGrow={1} minWidth={260}>
              <HeaderCell>Name</HeaderCell>
              <Cell>
                {(rowData) => {
                  const id = Number(rowData?.id);
                  const isEditing = Number.isInteger(id) && id === Number(editingUserId) && editingUserField === 'name';
                  if (isEditing) {
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={editingUserName}
                          onChange={setEditingUserName}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitEditUser();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditUser();
                            }
                          }}
                          onBlur={() => {
                            void commitEditUser();
                          }}
                          size="sm"
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      className="ppc-inline-edit-cell"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditUser(rowData);
                      }}
                      title="Click to rename"
                      style={{ gap: 8 }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {rowData?.name ?? ''}
                        {rowData?.hasRates === false ? (
                          <Whisper
                            trigger="hover"
                            placement="top"
                            speaker={<Tooltip>No Rates Assigned</Tooltip>}
                          >
                            <span
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              style={{ display: 'inline-flex', alignItems: 'center', color: '#f39c12' }}
                              aria-label="No Rates Assigned"
                              title="No Rates Assigned"
                            >
                              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                            </span>
                          </Whisper>
                        ) : null}
                      </span>
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column flexGrow={1} minWidth={260}>
              <HeaderCell>Email</HeaderCell>
              <Cell>
                {(rowData) => {
                  const id = Number(rowData?.id);
                  const isEditing = Number.isInteger(id) && id === Number(editingUserId) && editingUserField === 'email';

                  if (isEditing) {
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Input
                          key={`email-${id}`}
                          defaultValue={editingUserEmail}
                          onChange={(value) => {
                            editingUserEmailDraftRef.current = String(value ?? '');
                          }}
                          placeholder="name@company.com"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitEditUser();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditUser();
                            }
                          }}
                          size="sm"
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      className="ppc-inline-edit-cell"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditUserEmail(rowData);
                      }}
                      title="Click to edit email"
                    >
                      {String(rowData?.email || '').trim() || '—'}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column width={180} align="right">
              <HeaderCell>
                <Stack direction="row" spacing={8} alignItems="center" justifyContent="flex-end">
                  <span>Reserved Hours</span>
                  <Button
                    size="xs"
                    appearance="subtle"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowReservedHoursInfo(true);
                    }}
                    title="What is this?"
                  >
                    <i className="bi bi-info-circle" aria-hidden="true" />
                  </Button>
                </Stack>
              </HeaderCell>
              <Cell>
                {(rowData) => {
                  const id = Number(rowData?.id);
                  const isEditing =
                    Number.isInteger(id) && id === Number(editingUserId) && editingUserField === 'reserved';

                  if (isEditing) {
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          max={100}
                          step={1}
                          precision={2}
                          value={editingUserReservedPercent}
                          onChange={(value) => {
                            const n = Number(value);
                            if (!Number.isFinite(n)) {
                              setEditingUserReservedPercent(0);
                              return;
                            }
                            setEditingUserReservedPercent(Math.min(100, Math.max(0, n)));
                          }}
                          size="sm"
                          onFocus={(e) => {
                            queueMicrotask(() => e?.target?.select?.());
                          }}
                          onClick={(e) => {
                            queueMicrotask(() => e?.target?.select?.());
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitEditUser();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditUser();
                            }
                          }}
                        />
                      </div>
                    );
                  }

                  const raw = Number(rowData?.ftePercent ?? rowData?.fte_percent ?? 100);
                  const fte = Number.isFinite(raw) ? raw : 100;
                  const reserved = 100 - fte;
                  const v = Number.isFinite(reserved) ? reserved : 0;
                  return (
                    <div
                      className="ppc-inline-edit-cell"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditUserFte(rowData);
                      }}
                      title="Click to edit"
                      style={{ justifyContent: 'flex-end', width: '100%' }}
                    >
                      {`${v.toFixed(v % 1 === 0 ? 0 : 2)}%`}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column width={90} align="center" fixed="right">
              <HeaderCell />
              <Cell>
                {(rowData) => (
                  <Stack direction="row" spacing={6} alignItems="center" justifyContent="center">
                    {Number(rowData?.id) === Number(editingUserId) ? (
                      <>
                        <Button
                          size="xs"
                          appearance="subtle"
                          loading={upsertUser.isPending}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              await commitEditUser();
                            } catch (err) {
                              const msg = String(err?.message || 'Failed to save user.');
                              toaster.push(
                                <Message type="error" showIcon>
                                  {msg}
                                </Message>,
                                { placement: 'topEnd', duration: 4500 }
                              );
                            }
                          }}
                          title="Save"
                        >
                          <i className="bi bi-check2" aria-hidden="true" />
                        </Button>
                        <Button
                          size="xs"
                          appearance="subtle"
                          disabled={upsertUser.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            cancelEditUser();
                          }}
                          title="Cancel"
                        >
                          <i className="bi bi-x" aria-hidden="true" />
                        </Button>
                      </>
                    ) : (
                      <Whisper
                        trigger="click"
                        placement="bottomEnd"
                        container={() => document.body}
                        speaker={(speakerProps, ref) => {
                          const { onClose, className, style, ...rest } = speakerProps;
                          return (
                            <Popover {...rest} ref={ref} className={className} full style={{ ...style, zIndex: 99999 }}>
                              <Menu
                                onSelect={async (eventKey, event) => {
                                  event?.preventDefault?.();
                                  event?.stopPropagation?.();
                                  onClose?.();

                                  if (eventKey === 'rates') {
                                    goToUser(rowData);
                                    return;
                                  }

                                  if (eventKey === 'delete') {
                                    requestDeleteUser(rowData);
                                  }
                                }}
                              >
                                <Menu.Item eventKey="rates">Edit Rates</Menu.Item>
                                <Menu.Separator />
                                <Menu.Item eventKey="delete" style={{ color: '#c62828' }}>
                                  Delete
                                </Menu.Item>
                              </Menu>
                            </Popover>
                          );
                        }}
                      >
                        <Button
                          size="xs"
                          appearance="subtle"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          title="Actions"
                          aria-label="Actions"
                          disabled={deleteUser.isPending}
                        >
                          <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                        </Button>
                      </Whisper>
                    )}
                  </Stack>
                )}
              </Cell>
            </Column>
          </Table>
        </Panel>

        <Divider />

        {projectsError ? (
          <Message type="error" showIcon>
            Failed to load projects.
          </Message>
        ) : null}

        {ratesError ? (
          <Message type="error" showIcon>
            Failed to load rates.
          </Message>
        ) : null}

        <Panel
          bordered
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>Rates</span>
              <Button
                appearance="subtle"
                size="sm"
                onClick={() => {
                  setRatesProjectId(null);
                }}
              >
                Clear Filter
              </Button>
            </div>
          }
          style={{ marginBottom: 12 }}
        >
          <Stack direction="row" spacing={12} alignItems="center" wrap>
            <div style={{ fontSize: 12, color: '#666', minWidth: 120 }}>Project</div>
            <SelectPicker
              searchable
              cleanable
              placeholder="All projects"
              loading={projectsLoading}
              value={ratesProjectId}
              data={projectRows
                .map((p) => ({ label: p?.name ?? '', value: Number(p?.id) }))
                .filter((p) => Number.isInteger(p.value) && p.label)}
              onChange={(v) => {
                if (v === null || v === undefined || v === '') {
                  setRatesProjectId(null);
                  return;
                }
                const n = Number(v);
                setRatesProjectId(Number.isInteger(n) ? n : null);
              }}
              style={{ minWidth: 320 }}
            />

            <Checkbox checked={ratesOnlyCurrent} onChange={(_value, checked) => setRatesOnlyCurrent(checked)}>
              Only show current
            </Checkbox>

            <div style={{ fontSize: 12, color: '#666' }}>{visibleRatesRows.length} rows</div>
          </Stack>

          <Divider style={{ margin: '12px 0' }} />

          <Table data={visibleRatesRows} rowKey="key" autoHeight bordered cellBordered loading={ratesLoading}>
            <Column flexGrow={1} minWidth={220}>
              <HeaderCell>Project</HeaderCell>
              <Cell dataKey="projectName" />
            </Column>

            <Column flexGrow={1} minWidth={180}>
              <HeaderCell>User</HeaderCell>
              <Cell dataKey="userName" />
            </Column>

            <Column width={130} align="left">
              <HeaderCell>Effective</HeaderCell>
              <Cell dataKey="effectiveDate" />
            </Column>

            <Column width={180} align="right">
              <HeaderCell>Rate</HeaderCell>
              <Cell>
                {(rowData) => {
                  const n = Number(rowData?.rate);
                  if (!Number.isFinite(n)) return '';
                  const uid = Number(rowData?.userId);
                  const pid = Number(rowData?.projectId);
                  const eff = String(rowData?.effectiveDate ?? '').slice(0, 10);
                  const key = `${uid}-${pid}`;
                  const currentEff = currentEffectiveDateByUserProject.get(key);
                  const isCurrent =
                    Number.isInteger(uid) &&
                    Number.isInteger(pid) &&
                    /^\d{4}-\d{2}-\d{2}$/.test(eff) &&
                    Boolean(currentEff) &&
                    eff === currentEff;

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                      {isCurrent ? <CurrentBadge /> : null}
                      {currencyFormatter.format(n)}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column width={130} align="center" fixed="right">
              <HeaderCell />
              <Cell>
                {(rowData) => (
                  <Button
                    size="xs"
                    appearance="subtle"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const userId = Number(rowData?.userId);
                      if (Number.isInteger(userId)) navigate(`/users/${userId}`);
                    }}
                    title="Edit this user's rates"
                  >
                    Edit
                  </Button>
                )}
              </Cell>
            </Column>
          </Table>

          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Tip: click Edit to manage historical rates (effective dates) per user.
          </div>
        </Panel>

        <Divider />

        {closeDatesError ? (
          <Message type="error" showIcon>
            Failed to load close dates.
          </Message>
        ) : null}

        <Panel
          bordered
          collapsible
          defaultExpanded={false}
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <Stack direction="row" spacing={6} alignItems="center">
                <span>Close Dates</span>
                <Button
                  appearance="subtle"
                  size="xs"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCloseDateSourceConfig(true);
                  }}
                  title="Configure close date source"
                  aria-label="Configure close date source"
                >
                  <i className="bi bi-gear" aria-hidden="true" />
                </Button>
              </Stack>
              <Button
                appearance="subtle"
                size="sm"
                disabled={!isManualCloseDateSource}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isManualCloseDateSource) return;
                  openAddCloseDate();
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Add
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
              </Button>
            </div>
          }
        >
          {isManualCloseDateSource ? (
            <Message type="info" showIcon>
              Close dates subtract 8 hours from the monthly workable-hours calculation. Source: {activeCloseDateSourceDisplayName}.
            </Message>
          ) : (
            <Message type="info" showIcon>
              EmployeeConnect is active. Close dates are synced read-only from EmployeeConnect.
            </Message>
          )}

          <Divider />

          <Table
            data={closeDateRows}
            rowKey="key"
            autoHeight
            bordered
            cellBordered
            loading={closeDatesLoading}
          >
            <Column width={140} align="left">
              <HeaderCell>Date</HeaderCell>
              <Cell>
                {(rowData) => {
                  const id = Number(rowData?.id);
                  const isEditing = Number.isInteger(id) && id === Number(editingCloseDateId);
                  if (isEditing) {
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <DatePicker
                          oneTap
                          format="MM/dd"
                          isoWeek={false}
                          weekStart={0}
                          value={iso10ToDate(editingCloseDateIso) ?? iso10ToDate(rowData?.closeDateIso)}
                          onChange={(d) => {
                            const iso = toIsoDate10(d);
                            if (iso) {
                              setEditingCloseDateIso(iso);
                              void commitEditCloseDate({ id, closeDateIso: iso });
                            }
                          }}
                          placeholder="MM/DD"
                          style={{ width: 120 }}
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      className="ppc-inline-edit-cell"
                      onClick={(e) => {
                        if (!isManualCloseDateSource) return;
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditCloseDate(rowData);
                      }}
                      title={isManualCloseDateSource ? 'Click to edit' : 'Read-only (EmployeeConnect source)'}
                    >
                      {rowData?.closeDate ?? ''}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column flexGrow={1} minWidth={260}>
              <HeaderCell>Description</HeaderCell>
              <Cell>
                {(rowData) => {
                  const id = Number(rowData?.id);
                  const isEditing = Number.isInteger(id) && id === Number(editingCloseDateId);
                  if (isEditing) {
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={editingCloseDateDesc}
                          onChange={setEditingCloseDateDesc}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitEditCloseDate();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditCloseDate();
                            }
                          }}
                          onBlur={() => {
                            void commitEditCloseDate();
                          }}
                          size="sm"
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      className="ppc-inline-edit-cell"
                      onClick={(e) => {
                        if (!isManualCloseDateSource) return;
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditCloseDate(rowData);
                      }}
                      title={isManualCloseDateSource ? 'Click to edit' : 'Read-only (EmployeeConnect source)'}
                    >
                      {rowData?.description ?? ''}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column width={90} align="center" fixed="right">
              <HeaderCell />
              <Cell>
                {(rowData) => (
                  <Stack direction="row" spacing={6} alignItems="center" justifyContent="center">
                    {Number(rowData?.id) === Number(editingCloseDateId) ? (
                      <>
                        <Button
                          size="xs"
                          appearance="subtle"
                          loading={upsertCloseDate.isPending || deleteCloseDate.isPending}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              await commitEditCloseDate();
                            } catch (err) {
                              const msg = String(err?.message || 'Failed to save close date.');
                              toaster.push(
                                <Message type="error" showIcon>
                                  {msg}
                                </Message>,
                                { placement: 'topEnd', duration: 4500 }
                              );
                            }
                          }}
                          title="Save"
                        >
                          <i className="bi bi-check2" aria-hidden="true" />
                        </Button>
                        <Button
                          size="xs"
                          appearance="subtle"
                          disabled={upsertCloseDate.isPending || deleteCloseDate.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            cancelEditCloseDate();
                          }}
                          title="Cancel"
                        >
                          <i className="bi bi-x" aria-hidden="true" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="xs"
                        appearance="subtle"
                        loading={deleteCloseDate.isPending}
                        disabled={!isManualCloseDateSource || !Number.isInteger(Number(rowData?.id))}
                        onClick={async (e) => {
                          if (!isManualCloseDateSource) return;
                          e.preventDefault();
                          e.stopPropagation();
                          requestDeleteCloseDate(rowData);
                        }}
                        title={isManualCloseDateSource ? 'Delete' : 'Read-only (EmployeeConnect source)'}
                      >
                        <i className="bi bi-trash" aria-hidden="true" />
                      </Button>
                    )}
                  </Stack>
                )}
              </Cell>
            </Column>
          </Table>
        </Panel>

        <Divider />

        {trashedProjectsError ? (
          <Message type="error" showIcon>
            Failed to load trashed projects.
          </Message>
        ) : null}

        <Panel
          bordered
          collapsible
          defaultExpanded={false}
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className="bi bi-trash" aria-hidden="true" />
              <span>Project Trash</span>
              <Badge content={trashedProjects.length} />
            </div>
          }
        >
          <Message type="info" showIcon>
            Projects are permanently deleted 30 days after being trashed. Restore a project at any time before then.
          </Message>

          <Divider style={{ margin: '12px 0' }} />

          <Table
            data={trashedProjects}
            rowKey="id"
            autoHeight
            bordered
            cellBordered
            loading={trashedProjectsLoading}
          >
            <Column flexGrow={1} minWidth={180}>
              <HeaderCell>Project</HeaderCell>
              <Cell dataKey="name" />
            </Column>

            <Column width={160}>
              <HeaderCell>Deleted</HeaderCell>
              <Cell>{(rowData) => formatTrashDate(rowData?.deletedAt)}</Cell>
            </Column>

            <Column width={160}>
              <HeaderCell>Expires</HeaderCell>
              <Cell>{(rowData) => formatTrashDate(rowData?.trashExpiresAt)}</Cell>
            </Column>

            <Column width={220} align="center" fixed="right">
              <HeaderCell />
              <Cell>
                {(rowData) => (
                  <Stack spacing={6} alignItems="center" justifyContent="center">
                    <Button
                      size="xs"
                      appearance="subtle"
                      loading={restoreProject.isPending}
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const projectId = Number(rowData?.id);
                        if (!Number.isInteger(projectId)) return;
                        try {
                          await restoreProject.mutateAsync(projectId);
                          toaster.push(
                            <Message type="success" showIcon>
                              Project restored.
                            </Message>,
                            { placement: 'topEnd', duration: 2500 }
                          );
                        } catch (err) {
                          toaster.push(
                            <Message type="error" showIcon>
                              {String(err?.message || 'Failed to restore project.')}
                            </Message>,
                            { placement: 'topEnd', duration: 4500 }
                          );
                        }
                      }}
                    >
                      Restore
                    </Button>
                    <Button
                      size="xs"
                      appearance="ghost"
                      color="red"
                      loading={permanentDeleteProject.isPending}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setConfirmPermDeleteRow(rowData);
                        setConfirmPermDeleteOpen(true);
                      }}
                    >
                      Permanent Delete
                    </Button>
                  </Stack>
                )}
              </Cell>
            </Column>
          </Table>
        </Panel>

        <Modal open={showProviderConfig} onClose={() => setShowProviderConfig(false)} size="sm">
          <Modal.Header>
            <Modal.Title>
              Configure {providerRows.find((p) => p.name === selectedProviderName)?.displayName || selectedProviderName}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedProviderName === 'timecamp' ? (
              <div className="ppc-provider-config-form">
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>API Token</div>
                <Input
                  type="password"
                  value={providerConfig?.apiToken || ''}
                  onChange={(val) => setProviderConfig({ ...providerConfig, apiToken: val })}
                  placeholder="Enter your TimeCamp API token"
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                  Find your API token in TimeCamp Settings → API
                </div>
              </div>
            ) : selectedProviderName === 'manual' ? (
              <Message type="info" showIcon>
                Manual entry requires no configuration. Click Save to activate.
              </Message>
            ) : (
              <Message type="warning" showIcon>
                Select a provider to configure.
              </Message>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              appearance="ghost"
              loading={testingConnection}
              onClick={testProviderConnection}
              disabled={selectedProviderName === 'manual' || !selectedProviderName}
            >
              Test Connection
            </Button>
            <Button
              appearance="primary"
              loading={setActiveProvider.isPending}
              onClick={saveProviderConfig}
              disabled={
                !selectedProviderName ||
                (selectedProviderName === 'timecamp' && !String(providerConfig?.apiToken || '').trim())
              }
            >
              Save &amp; Activate
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setShowProviderConfig(false)}
              disabled={setActiveProvider.isPending}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </Panel>

      <Modal open={showReservedHoursInfo} onClose={() => setShowReservedHoursInfo(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Reserved Hours</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This is the percent of total hours that should be used for the utilization calculation. For example, if a
          user has 20% reserved hours, they are averaging 6 hours of work per day.
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={() => setShowReservedHoursInfo(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={showCloseDateSourceConfig} onClose={() => setShowCloseDateSourceConfig(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Close Date Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Message type="info" showIcon>
              If you have access to EmployeeConnect you may choose to pull company close dates from there, otherwise they can be entered manually in here.
            </Message>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Source</div>
              <SelectPicker
                block
                searchable={false}
                cleanable={false}
                data={closeDateSourceRows.map((s) => ({
                  label: s?.displayName || s?.name,
                  value: s?.name,
                  disabled: s?.isConfigured === false,
                }))}
                value={selectedCloseDateSourceName || null}
                onChange={(v) => setSelectedCloseDateSourceName(String(v || ''))}
                loading={closeDateSourcesLoading || activeCloseDateSourceLoading}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {selectedSourceIsEmployeeConnect ? (
            <Button
              appearance="ghost"
              loading={pullingEmployeeConnectCloseDates}
              disabled={selectedCloseDateSource?.isConfigured === false || setActiveCloseDateSource.isPending}
              onClick={async () => {
                await pullFromEmployeeConnect();
              }}
            >
              Pull
            </Button>
          ) : null}

          <Button
            appearance="primary"
            loading={setActiveCloseDateSource.isPending}
            disabled={!selectedCloseDateSourceName || selectedCloseDateSource?.isConfigured === false}
            onClick={async () => {
              try {
                await saveCloseDateSource();
                setShowCloseDateSourceConfig(false);
              } catch (err) {
                toaster.push(
                  <Message type="error" showIcon>
                    {String(err?.message || 'Failed to update close date source.')}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Confirm
          </Button>
          <Button
            appearance="subtle"
            disabled={setActiveCloseDateSource.isPending || pullingEmployeeConnectCloseDates}
            onClick={() => setShowCloseDateSourceConfig(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={addUserOpen} onClose={() => setAddUserOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Add User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, color: '#666' }}>Name</div>
            <Input value={newUserName} onChange={setNewUserName} placeholder="e.g. John Smith" />
            <div style={{ fontSize: 12, color: '#666' }}>Email</div>
            <Input value={newUserEmail} onChange={setNewUserEmail} placeholder="e.g. john.smith@company.com" />
            <div style={{ fontSize: 12, color: '#666' }}>AAD OID (optional)</div>
            <Input
              value={newUserAadOid}
              onChange={setNewUserAadOid}
              placeholder="e.g. 00000000-0000-0000-0000-000000000000"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitAddUser} loading={upsertUser.isPending}>
            Create
          </Button>
          <Button appearance="subtle" onClick={() => setAddUserOpen(false)} disabled={upsertUser.isPending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteUserOpen}
        onClose={() => {
          setConfirmDeleteUserOpen(false);
          setConfirmDeleteUserRow(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete user?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Message type="warning" showIcon>
              This will remove all projects for this person (their project rates will be deleted).
            </Message>
            <div>
              User: <strong>{String(confirmDeleteUserRow?.name ?? confirmDeleteUserRow?.userName ?? '—')}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            loading={deleteUser.isPending}
            onClick={async () => {
              try {
                await removeUser(confirmDeleteUserRow);
                setConfirmDeleteUserOpen(false);
                setConfirmDeleteUserRow(null);
              } catch (err) {
                const msg = String(err?.message || 'Failed to remove user.');
                toaster.push(
                  <Message type="error" showIcon>
                    {msg}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Delete
          </Button>
          <Button
            appearance="subtle"
            disabled={deleteUser.isPending}
            onClick={() => {
              setConfirmDeleteUserOpen(false);
              setConfirmDeleteUserRow(null);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteCloseDateOpen}
        onClose={() => {
          setConfirmDeleteCloseDateOpen(false);
          setConfirmDeleteCloseDateRow(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete close date?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Message type="warning" showIcon>
              This will change workable-hours and utilization calculations.
            </Message>
            <div>
              Date: <strong>{String(confirmDeleteCloseDateRow?.closeDate ?? '—')}</strong>
            </div>
            {String(confirmDeleteCloseDateRow?.description ?? '').trim() ? (
              <div>
                Description: <strong>{String(confirmDeleteCloseDateRow?.description ?? '')}</strong>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            loading={deleteCloseDate.isPending}
            onClick={async () => {
              try {
                await removeCloseDate(confirmDeleteCloseDateRow);
                setConfirmDeleteCloseDateOpen(false);
                setConfirmDeleteCloseDateRow(null);
              } catch (err) {
                const msg = String(err?.message || 'Failed to remove close date.');
                toaster.push(
                  <Message type="error" showIcon>
                    {msg}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Delete
          </Button>
          <Button
            appearance="subtle"
            disabled={deleteCloseDate.isPending}
            onClick={() => {
              setConfirmDeleteCloseDateOpen(false);
              setConfirmDeleteCloseDateRow(null);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmPermDeleteOpen}
        onClose={() => {
          setConfirmPermDeleteOpen(false);
          setConfirmPermDeleteRow(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Permanently delete project?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Message type="warning" showIcon>
              This will permanently delete the project and all its data. This cannot be undone.
            </Message>
            <div>
              Project: <strong>{String(confirmPermDeleteRow?.name ?? '—')}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            loading={permanentDeleteProject.isPending}
            onClick={async () => {
              const projectId = Number(confirmPermDeleteRow?.id);
              if (!Number.isInteger(projectId)) return;
              try {
                await permanentDeleteProject.mutateAsync(projectId);
                toaster.push(
                  <Message type="success" showIcon>
                    Project permanently deleted.
                  </Message>,
                  { placement: 'topEnd', duration: 2500 }
                );
                setConfirmPermDeleteOpen(false);
                setConfirmPermDeleteRow(null);
              } catch (err) {
                toaster.push(
                  <Message type="error" showIcon>
                    {String(err?.message || 'Failed to permanently delete project.')}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Delete Permanently
          </Button>
          <Button
            appearance="subtle"
            disabled={permanentDeleteProject.isPending}
            onClick={() => {
              setConfirmPermDeleteOpen(false);
              setConfirmPermDeleteRow(null);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={addCloseDateOpen} onClose={() => setAddCloseDateOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Add Close Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#666' }}>Date</div>
              <DatePicker
                oneTap
                format="MM/dd"
                isoWeek={false}
                weekStart={0}
                value={newCloseDate}
                onChange={setNewCloseDate}
                placeholder="MM/DD"
                style={{ width: 220 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#666' }}>Description</div>
              <Input
                value={newCloseDesc}
                onChange={setNewCloseDesc}
                placeholder="e.g. Christmas closure"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitAddCloseDate} loading={upsertCloseDate.isPending}>
            Save
          </Button>
          <Button appearance="subtle" onClick={() => setAddCloseDateOpen(false)} disabled={upsertCloseDate.isPending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
